<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';
import Button from 'dashboard/components-next/button/Button.vue';
import KanbanColumn from '../components/KanbanColumn.vue';
import { colorForIndex } from '../constants';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const attributeKey = computed(() => route.params.attributeKey);

const conversationAttributes = useMapGetter('attributes/getConversationAttributes');

// O atributo personalizado de conversa (tipo lista) que define este
// quadro específico — o board inteiro é derivado dele, sem nenhuma
// configuração extra: nome do atributo = nome do quadro, itens da
// lista = colunas, nessa mesma ordem.
const boardAttribute = computed(() =>
  conversationAttributes.value.find(
    attr => attr.attributeKey === attributeKey.value && attr.attributeDisplayType === 'list'
  )
);

const columns = computed(() => {
  if (!boardAttribute.value) return [];
  return (boardAttribute.value.attributeValues || []).map((value, index) => ({
    title: value,
    color: colorForIndex(index),
  }));
});

const conversationsByValue = reactive({});
const fetchingByValue = reactive({});
const isRefreshing = ref(false);
const isLoadingAttributes = ref(true);

const fetchColumn = async value => {
  fetchingByValue[value] = true;
  try {
    const response = await ConversationApi.filter({
      queryData: {
        payload: [
          {
            attribute_key: attributeKey.value,
            filter_operator: 'equal_to',
            values: [value],
            query_operator: null,
          },
        ],
      },
      page: 1,
    });
    conversationsByValue[value] = response.data?.payload || [];
  } catch (error) {
    useAlert(t('KANBAN.ERRORS.FETCH'));
  } finally {
    fetchingByValue[value] = false;
  }
};

const fetchAllColumns = async () => {
  isRefreshing.value = true;
  await Promise.all(columns.value.map(column => fetchColumn(column.title)));
  isRefreshing.value = false;
};

const handleMoveConversation = async ({ conversation, newColumnValue }) => {
  const previousAttributes = conversation.custom_attributes || {};
  if (previousAttributes[attributeKey.value] === newColumnValue) return;

  // Só sobrescrevemos a CHAVE deste atributo — preservamos todas as
  // outras custom_attributes da conversa intactas (inclusive as de
  // outros quadros Kanban, que usam chaves diferentes).
  const newAttributes = {
    ...previousAttributes,
    [attributeKey.value]: newColumnValue,
  };

  try {
    await ConversationApi.updateCustomAttributes({
      conversationId: conversation.id,
      customAttributes: newAttributes,
    });

    const previousValue = previousAttributes[attributeKey.value];
    if (previousValue && conversationsByValue[previousValue]) {
      conversationsByValue[previousValue] = conversationsByValue[previousValue].filter(
        item => item.id !== conversation.id
      );
    }

    conversationsByValue[newColumnValue] = [
      { ...conversation, custom_attributes: newAttributes },
      ...(conversationsByValue[newColumnValue] || []).filter(
        item => item.id !== conversation.id
      ),
    ];
  } catch (error) {
    useAlert(t('KANBAN.ERRORS.UPDATE_LABELS'));
    fetchColumn(newColumnValue);
    if (previousAttributes[attributeKey.value]) {
      fetchColumn(previousAttributes[attributeKey.value]);
    }
  }
};

onMounted(async () => {
  isLoadingAttributes.value = true;
  await store.dispatch('attributes/get');
  isLoadingAttributes.value = false;
  await fetchAllColumns();
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 bg-n-background">
    <header
      class="flex items-center justify-between flex-shrink-0 px-4 py-3 border-b border-n-weak"
    >
      <div>
        <h1 class="text-base font-medium text-n-slate-12 m-0">
          {{ boardAttribute ? boardAttribute.attributeDisplayName : t('KANBAN.HEADER.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11 m-0">
          {{ t('KANBAN.HEADER.SUBTITLE') }}
        </p>
      </div>
      <Button
        icon="i-lucide-refresh-cw"
        color="slate"
        variant="ghost"
        size="sm"
        :is-loading="isRefreshing"
        :label="t('KANBAN.HEADER.REFRESH')"
        @click="fetchAllColumns"
      />
    </header>

    <div
      v-if="!isLoadingAttributes && !boardAttribute"
      class="flex flex-col items-center justify-center flex-grow gap-2 px-4 text-center"
    >
      <p class="text-sm text-n-slate-11 max-w-sm">
        {{ t('KANBAN.EMPTY_STATE.ATTRIBUTE_NOT_FOUND') }}
      </p>
    </div>

    <div v-else class="flex flex-grow min-h-0 gap-4 p-4 overflow-x-auto">
      <KanbanColumn
        v-for="column in columns"
        :key="column.title"
        :column="column"
        :conversations="conversationsByValue[column.title] || []"
        :is-fetching="!!fetchingByValue[column.title]"
        @move-conversation="handleMoveConversation"
      />
    </div>
  </div>
</template>
